import { Observable } from 'rxjs';
import { Organization, OrgNotDeterminedYet } from './account/org.types';
import { AuthenticatedUser } from './account/user.types';

export const enum FeatureFlag {
  IFrameFSNoReload = 'iframe-fs-no-reload',
  UseTronLegacyCharts = 'use-tron-legacy-charts',
  DisableFlamingoDisplayTable = 'disable-flamingo-display-table',
  VirtualizedReportDisableDomCache = 'virtualized-report-disable-dom-cache',
  AlgoliaKeyDuration = 'algolia-key-duration',
  SkipReportAccountResolver = 'skip-report-account-resolver',
  LimitReportPropertyList = 'limit-report-property-list',
  AzureActiveDirectoryScim = 'azure-active-directory-scim',
  MyCollectionsSearch = 'my-collections-search',
  FastReportView = 'fast-report-view',
  RouteTrafficToEcs = 'route-traffic-to-ecs-flamingo',
  CsvExportRowLimit = 'csv-export-row-limit',
  FlamingoRequestLimit = 'perm-webapp-DASH-172-flamingo-request-limit',
  EnableCustomReportVirtualization = 'enable-custom-report-virtualization',
  NewReportViewerTracing = 'temp-new-report-viewer-tracing',
  UnaggregatedChartExport = 'unaggregated-chart-export',
  CustomPageSizeEnabled = 'adjust-flat-tables-page-size',
  AutoRedirectToProvidedSSO = 'temp-webapp-2013.11-jira-PLG-414-auto-redirect-to-sso-provided-by-query-params',
  HistogramQuickChart = 'histogram-quick-chart',
  LibraryCompatibilityTestingFlag = 'library-compatibility-testing-flag',

  // Reports & Datasets
  AlamodeErrorReporting = 'perm-webapp-05.2023-jira-RAD-791-enable-alamode-error-reports',
  ReportRunToast = 'temp-webapp-2023.07-github-rad-report-view-run-toast',
  RemoveDatasetSideEffects = 'temp-webapp-2023.07-jira-rad-796',
  ReportFilterDropdownEnabled = 'report-filter-dropdowns',
  ReportFiltersMigration = 'report-filters-migration',
  ReportFiltersDragAndDrop = 'report-filters-drag-and-drop',
  ReportFiltersDisplayRelevantValues = 'report-filters.display-relevant-values-enabled',
  ReportFiltersApplyAndGenerateLink = 'temp-webapp-share-report-with-pre-selected-filters',

  // Report Capture
  RenderingReporterEventDelay = 'perm-webapp-2023.02-github-17723-report-rendered-event-delay',
  LongCappieWorkEvent = 'perm-webapp-2023.05-jira-da559-long-cappie-work-event',
  ShowReportParametersForCapture = 'perm-webapp-2023.04-github-18712-optional-report-parameters-during-capture',

  // User & Content Management
  LoggedOutSignInFlow = 'logged-out-sign-in-flow',
  LoggedOutTracking = 'logged-out-tracking',
  EnableDiscoverPageForStudioPlan = 'temp-webapp-2023-plg-enable-discover-page-for-studio-plan',
  BulkScheduleManagement = 'temp-webapp-github-plg-266-bulk-schedule-management',
  AngularSignupRevamp = 'temp-webapp-2023-jira-plg-403-signup-migration',
  AdminManagedScheduleIntervals = 'temp-webapp-2024.12-NIDE-477-admin-managed-schedule-intervals',

  // VizExplorer
  VizExplorerMaxElementCount = 'viz-explorer-max-element-count',
  VizExplorerMaxFacetsPerPage = 'viz-explorer-max-facets-per-page',
  DatasetFieldSettingsFormattingColumnEnabled = 'dataset-field-settings-formatting-column-enabled',
  VizExplorerSubtotals = 'temp-webapp-2024.05-jira-MAC-1531-subtotals',

  // Query Editor
  AIAssistEnabled = 'temp-webapp-2023.06-ai-features',

  // OpenSearch migration
  AlgoliaToOpenSearchAutocomplete = 'temp-webapp-2024.12-jira-daq-1130',
  AlgoliaToOpenSearchSchemaBrowser = 'temp-webapp-2025.3-jira-daq-1130',
  OpenSearchAutocompleteMigration = 'open-search-autocomplete-migration',
  OpenSearchSchemaBrowserMigration = 'open-search-schema-browser-migration',

  // Customer Acquisition
  HideFreePlanSelection = 'temp-510-hide-free-plan-selection-for-signup-flow',

  // Notebooks
  RegisterSecureCredentialsNotebooks = 'temp-webapp-2024.07-jira-NIDE-94-register-secure-credential-notebooks',
  ShowCteAutocomplete = 'temp-webapp-2024.07-jira-NIDE-161-add-ctes-in-auto-complete',
  NotebookPublishExportedDatasets = 'temp-webapp-2024.10-jira-NIDE-447-PublishLocalDatasetToTs',
  NotebookDatasetPublishUpdates = 'temp-webapp-2025.05-SCAL-249050-notebook-dataset-publish-updates',
  NotebookMigrationPhase1 = 'temp-nide-980-complete-notebook-migration-phase-1',
  NotebookLibraryCleanslate = 'temp-webapp-2025.05-JIRA-NIDE-916-py3-cleanslate',
  // Frontend Observability
  WebVitalsFrontendObservability = 'temp-webapp-NIDE-258-webvitals-frontend-observability',
  // Excel Sheet Dataset Upload
  ExcelSheetDatasetUpload = 'temp-webapp-2025.07-SCAL-253436-excel-sheet-dataset',
  // File Upload Dataset
  FileUploadDataset = 'temp-webapp-2025.11-SCAL-278073-file-upload-dataset',
  // File Upload Dataset 5GB Upload Limit
  FileUploadDataset5GbUploadLimit = 'temp-webapp-2026.04-SCAL-306686-5gb-upload',
  // Workspace Size
  WorkspaceSize = 'temp-webapp-2026.02-SCAL-295390-workspace-size',
  // Spreadsheet
  ProjectGridironSpreadsheetInterface = 'Project-Gridiron-Spreadsheet-Interface',
  PublishFromSpreadsheet = 'temp-scal-300372-enable-publish-button-for-spreadsheet',
  AggregateCalculatedFieldsInSpreadsheets = 'temp-webapp-2026.04-aggregate-calc-fields-in-spreadsheets',
  // MAC - Mode Application Core
  NewReportLyftBug = 'temp-webapp-2024.05-jira-MAC-1455-new-report-lyft-bug',
  DataRetention = 'temp-webapp-2025.02-jira-MAC-2195',

  // Dataset Publication
  DatasetPublishing = 'temp-webapp-2024.08-DAQ-1392-dataset-publishing',
  DatasetUnpublishing = 'temp-webapp-2024.12-DAQ-1402-dataset-unpublishing',

  // AI Code Completion
  PythonNotebooksCodeCompletion = 'temp-webapp-2025.07-jira-NIDE-1086-CodeCompletions',

  // dependency objects
  showDependencyObjects = 'temp-webapp-sep-2025-SCAL-271389-dependecy-object-for-published-DS',

  // Report Builder with embedded host
  EmbedBuilderWithEmbeddedHost = 'temp-webapp-2025.08-MAC-1548-embed-builder-with-embedded-host',
  SandboxIframeBypassCOOP = 'temp-webapp-2025.12-jira-SCAL-288129-sandbox-iframe-bypass-coop',

  // Autocomplete debouncing
  SqlEditorDebounce = 'sql-editor-debounce',
  //monaco editor
  UseMonacoEditorSQL = 'use-monaco-editor-sql',

  // Derived Queries
  DerivedQueriesEnabled = 'temp-webapp-2025.11-jira-SCAL-171621-derived-queries',

  // Support
  HasSupportAccess = 'perm-webapp-2025-12-jira-SCAL-286637-has-support-access',

  // White-Label Embed CSV Export
  WleCsvExport = 'temp-2026.02-SCAL-293927-wle-filtered-export',
}

export type AllFlags = {
  readonly [K in FeatureFlag]: boolean;
};

export abstract class FeatureFlagsFacade {
  abstract allFlags$: Observable<AllFlags>;
  abstract asObservable(feature: FeatureFlag): Observable<boolean>;
  abstract featureValueAsObservable<Type extends string | number | boolean>(feature: FeatureFlag): Observable<Type>;
  abstract asPromise(feature: FeatureFlag): Promise<boolean | undefined>;

  abstract init(allFlags: AllFlags): void;
}

export interface AccountsNeededForInitialization {
  organization: Organization | OrgNotDeterminedYet;
  user?: AuthenticatedUser;
}

export abstract class FeatureFlagsInitializationService {
  abstract initFlags(accounts: AccountsNeededForInitialization): Promise<void>;
}

// https://launchdarkly.com/docs/home/flags/migration
export type MigrationFlag = 'off' | 'shadow' | 'live' | 'complete';
